/**
 * Drawing the author into their own recording.
 *
 * The bubble is composed into the frame before `MediaRecorder` ever sees it, so what reaches Mux
 * is one ordinary video that plays the same everywhere — the web player, the native app, a file
 * someone downloads. The price is that the bubble is final: its position, its size and its
 * presence are pixels by the time the recording exists, and no later edit can reach them.
 *
 * `requestAnimationFrame` is not involved, and that is the whole reason this approach works. A
 * recording tab is expected to be hidden — the author is demonstrating another application — and
 * a hidden document is never sent a frame callback. Here every arriving camera frame drives
 * exactly one composition step instead, on a task queue the background throttling does not reach.
 */

/**
 * The breakout-box constructors, absent from TypeScript's DOM library because neither is on the
 * standards track: `MediaStreamTrackProcessor` is a working draft Chromium ships with a different
 * exposure than the specification describes, and `MediaStreamTrackGenerator` appears in no
 * specification at all. Both are declared here rather than cast at each call site, and the whole
 * composition below touches them in exactly two places, so the standardised `VideoTrackGenerator`
 * can take over without the drawing knowing.
 */
interface TrackProcessor {
	readonly readable: ReadableStream<VideoFrame>;
}
interface TrackGenerator extends MediaStreamTrack {
	readonly writable: WritableStream<VideoFrame>;
}
declare const MediaStreamTrackProcessor: {
	new (init: { track: MediaStreamTrack; maxBufferSize?: number }): TrackProcessor;
};
declare const MediaStreamTrackGenerator: {
	new (init: { kind: "video" }): TrackGenerator;
};

/** Where the bubble sits in a recorded frame, in that frame's own pixels. */
export interface BubbleGeometry {
	/** Diameter. The bubble is a circle, so width and height are the same number. */
	size: number;
	x: number;
	y: number;
}

/** Of the shorter side of the shared surface. Large enough to read a face, small enough to talk over. */
const BUBBLE_SHARE = 0.22;
/** Below this a face stops being recognisable at all, so a small surface gets a proportionally larger bubble. */
const BUBBLE_MIN_SIZE = 160;
/** Above this the bubble competes with what it is meant to be narrating. */
const BUBBLE_MAX_SIZE = 360;
/** Of the bubble's own diameter, so the inset scales with it rather than with the screen. */
const BUBBLE_MARGIN_SHARE = 0.22;

/**
 * Where to draw the bubble on a surface of this size.
 *
 * Derived rather than fixed, because a constant in pixels is two different things on a 720p window
 * and a 4K monitor. Both bounds are themselves capped against the surface, so the bubble fits even
 * on a shared window smaller than the minimum.
 *
 * @param width - The recorded frame's width in pixels.
 * @param height - The recorded frame's height in pixels.
 * @returns The bubble's diameter and its top-left corner, anchored bottom-right.
 */
export function bubbleGeometry(width: number, height: number): BubbleGeometry {
	const shortest = Math.max(1, Math.min(width, height));
	const upper = Math.min(BUBBLE_MAX_SIZE, shortest / 2);
	const lower = Math.min(BUBBLE_MIN_SIZE, upper);
	const size = Math.round(Math.min(Math.max(shortest * BUBBLE_SHARE, lower), upper));
	const margin = Math.round(size * BUBBLE_MARGIN_SHARE);
	return {
		size,
		x: Math.max(0, Math.round(width - size - margin)),
		y: Math.max(0, Math.round(height - size - margin)),
	};
}

/** Whether this browser can compose a camera bubble into a recording at all. */
export function isCameraBubbleSupported() {
	return (
		typeof MediaStreamTrackProcessor !== "undefined" &&
		typeof MediaStreamTrackGenerator !== "undefined" &&
		typeof OffscreenCanvas !== "undefined" &&
		typeof VideoFrame !== "undefined"
	);
}

/** A running composition, and the two things its owner needs from it. */
export interface CameraBubbleCompositor {
	/** The composed track — what the recorder records. */
	track: MediaStreamTrack;
	/** Stop and resume drawing without releasing either capture. */
	setPaused: (isPaused: boolean) => void;
	/** End the composition and release every frame it still holds. */
	stop: () => void;
}

/**
 * Draw a camera track into the corner of a screen track, and answer the result as one track.
 *
 * **The camera is the clock.** Every frame it delivers produces exactly one composed frame, and
 * the screen's most recent frame is drawn underneath. The reverse — driving the composition from
 * the screen — looks equivalent and is not: a motionless shared surface delivers roughly one frame
 * per second, which for a tutorial is most of the time, and the recording would stutter to match.
 * It also means the camera's own cadence is the recording's, which is why the caller constrains
 * its frame rate rather than letting a dim room decide.
 *
 * @param screen - The shared surface's video track.
 * @param camera - The camera's video track.
 * @returns The composed track with the controls for pausing and ending it.
 */
export function composeScreenWithCamera(
	screen: MediaStreamTrack,
	camera: MediaStreamTrack,
): CameraBubbleCompositor {
	const screenReader = new MediaStreamTrackProcessor({ track: screen }).readable.getReader();
	const cameraFrames = new MediaStreamTrackProcessor({ track: camera }).readable;
	const generator = new MediaStreamTrackGenerator({ kind: "video" });
	const canvas = new OffscreenCanvas(1, 1);
	const context = canvas.getContext("2d", { alpha: false });

	let latestScreenFrame: VideoFrame | null = null;
	let isReadingScreen = false;
	let isPaused = false;
	let isStopped = false;

	function releaseScreenFrame() {
		latestScreenFrame?.close();
		latestScreenFrame = null;
	}

	function stop() {
		if (isStopped) return;
		isStopped = true;
		releaseScreenFrame();
		void screenReader.cancel().catch(() => undefined);
		generator.stop();
	}

	/**
	 * Ask for the next screen frame without waiting for it. The guard is what keeps a still
	 * surface from queueing one unresolved read per camera frame — thirty a second, none of which
	 * will settle until something on screen moves.
	 */
	function pumpScreenFrame() {
		if (isReadingScreen || isStopped) return;
		isReadingScreen = true;
		screenReader
			.read()
			.then(({ value }) => {
				isReadingScreen = false;
				if (!value) return;
				if (isStopped) {
					value.close();
					return;
				}
				latestScreenFrame?.close();
				latestScreenFrame = value;
			})
			.catch(() => {
				isReadingScreen = false;
			});
	}

	const composition = new TransformStream<VideoFrame, VideoFrame>({
		async transform(cameraFrame, controller) {
			// First statement, and not defensive noise: once the generator's track has stopped, the
			// next write into it throws rather than being ignored.
			if (isStopped || screen.readyState === "ended" || generator.readyState === "ended") {
				cameraFrame.close();
				releaseScreenFrame();
				controller.terminate();
				return;
			}

			// Pumped before the pause check, so a paused recording keeps draining the screen's
			// queue instead of resuming against a frame from minutes ago.
			if (latestScreenFrame) {
				pumpScreenFrame();
			} else {
				// The one frame worth waiting for: the canvas has no size until a screen frame says
				// what it is. Every later frame is read without blocking, because a still surface
				// delivers about one a second and awaiting it would hold the whole recording there.
				const { value } = await screenReader.read();
				latestScreenFrame = value ?? null;
			}

			const screenFrame = latestScreenFrame;
			if (!screenFrame || !context) {
				cameraFrame.close();
				return;
			}

			if (isPaused) {
				cameraFrame.close();
				return;
			}

			// Assigning the size resets the whole 2D context, clearing the bitmap along with the
			// clipping region — which is why it happens every frame and not only when the surface
			// is resized. The `save`/`restore` pair below makes the clip correct on its own terms,
			// so this assignment can be optimised later without the bubble eating the screen.
			canvas.width = screenFrame.displayWidth;
			canvas.height = screenFrame.displayHeight;
			context.drawImage(screenFrame, 0, 0);
			drawBubble(context, cameraFrame, bubbleGeometry(canvas.width, canvas.height));

			const composed = new VideoFrame(canvas, { timestamp: cameraFrame.timestamp });
			cameraFrame.close();
			// The generator closes the frame it is handed; closing it here as well would revoke it
			// before the track has read it.
			controller.enqueue(composed);
		},

		flush() {
			releaseScreenFrame();
		},
	});

	void cameraFrames
		.pipeThrough(composition)
		.pipeTo(generator.writable)
		.catch((error) => {
			// Stopping the recording ends the generator's track and rejects the write in flight.
			// That is the ordinary end, not a failure.
			if (isStopped) return;
			console.error("Composing the camera into the recording failed:", error);
			stop();
		});

	return {
		track: generator,
		setPaused: (paused) => {
			isPaused = paused;
		},
		stop,
	};
}

/**
 * Draw one camera frame as a circle in the given place.
 *
 * The source is cropped to a centred square first, so a 16:9 camera is not squashed into a round
 * hole. The clip is opened and closed with a real `save`/`restore` pair: relying on the canvas
 * resize to clear it works, right up until somebody notices the resize is redundant and removes
 * it, at which point every frame after the first is the bubble alone on black.
 */
function drawBubble(
	context: OffscreenCanvasRenderingContext2D,
	frame: VideoFrame,
	{ size, x, y }: BubbleGeometry,
) {
	const side = Math.min(frame.displayWidth, frame.displayHeight);
	const sourceX = (frame.displayWidth - side) / 2;
	const sourceY = (frame.displayHeight - side) / 2;

	context.save();
	context.beginPath();
	context.roundRect(x, y, size, size, size / 2);
	context.clip();
	context.drawImage(frame, sourceX, sourceY, side, side, x, y, size, size);
	context.restore();
}
